import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { searchPropertiesByLocation, formatPrice, formatSquareFootage } from '../services/locationSearchService';
import { getCitySuggestions } from '../services/awsConfig';
import Notification from '../components/Notification';
import '../styles/BrowsePropertiesPage.css';

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Property type icons mapping
const PROPERTY_TYPE_ICONS = {
  'Single Family': '🏠',
  'Condo': '🏢',
  'Townhouse': '🏘️',
  'Multi-Family': '🏛️',
  'Apartment': '🏬',
  'Manufactured': '🏡',
  'Land': '🌳',
  'default': '🏠'
};

const getPropertyIcon = (propertyType) => {
  return PROPERTY_TYPE_ICONS[propertyType] || PROPERTY_TYPE_ICONS['default'];
};

const getZillowUrl = (property) => {
  const address = `${property.addressLine1} ${property.city} ${property.state} ${property.zipCode}`
    .replace(/[,#]/g, '')
    .replace(/\s+/g, '-');
  return `https://www.zillow.com/homes/${encodeURIComponent(address)}_rb/`;
};

const BrowsePropertiesPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Guest search tracking (same as home page)
  const [guestSearchCount, setGuestSearchCount] = useState(
    parseInt(localStorage.getItem('guestSearchCount') || '0')
  );
  // Guest ROI calculation tracking (separate from search)
  const [guestROICount, setGuestROICount] = useState(
    parseInt(localStorage.getItem('guestBrowseROICount') || '0')
  );

  // Initialize state from URL params
  const [searchType, setSearchType] = useState(() =>
    searchParams.get('city') ? 'citystate' : 'zipcode'
  );
  const [zipCode, setZipCode] = useState(() => searchParams.get('zipCode') || '');
  const [city, setCity] = useState(() => searchParams.get('city') || '');
  const [state, setState] = useState(() => searchParams.get('state') || '');
  const [bedrooms, setBedrooms] = useState(() => searchParams.get('bedrooms') || 'any');
  const [bathrooms, setBathrooms] = useState(() => searchParams.get('bathrooms') || 'any');
  const [propertyType, setPropertyType] = useState(() => searchParams.get('propertyType') || 'any');
  const [minPrice, setMinPrice] = useState(() => searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(() => searchParams.get('maxPrice') || '');

  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  // Results state
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [initialSearchDone, setInitialSearchDone] = useState(false);

  const handleCityChange = async (e) => {
    const value = e.target.value;
    setCity(value);

    if (value.length >= 2) {
      try {
        const suggestions = await getCitySuggestions(value);
        setCitySuggestions(suggestions);
        setShowCitySuggestions(true);
      } catch (error) {
        console.error('Error fetching city suggestions:', error);
        setCitySuggestions([]);
      }
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleCitySuggestionClick = (suggestion) => {
    setCity(suggestion.city);
    // State is already a code (e.g., "OH", "TX")
    setState(suggestion.state);
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  // Core search function (reusable)
  const performSearch = useCallback(async (params, incrementGuestCount = false) => {
    setIsLoading(true);
    setHasSearched(true);

    try {
      const apiParams = {
        limit: 20,
        userId: user?.userId || null,
        ...params
      };

      const response = await searchPropertiesByLocation(apiParams);
      setResults(response.results || []);

      // Track guest searches (same as home page)
      if (incrementGuestCount && !user) {
        const newCount = guestSearchCount + 1;
        setGuestSearchCount(newCount);
        localStorage.setItem('guestSearchCount', newCount.toString());
      }

      if (response.results?.length === 0) {
        showNotification('No properties found matching your criteria', 'info');
      }
    } catch (error) {
      console.error('Search error:', error);
      showNotification(error.message || 'Error searching properties', 'error');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.userId, user, guestSearchCount]);

  // Update URL with search params
  const updateUrlParams = useCallback((params) => {
    const newParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== 'any') {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  // Run search on initial load if URL has params
  useEffect(() => {
    if (initialSearchDone) return;

    const hasUrlParams = searchParams.get('zipCode') || searchParams.get('city');
    if (hasUrlParams) {
      const params = {};
      if (searchParams.get('zipCode')) params.zipCode = searchParams.get('zipCode');
      if (searchParams.get('city')) params.city = searchParams.get('city');
      if (searchParams.get('state')) params.state = searchParams.get('state');
      if (searchParams.get('bedrooms') && searchParams.get('bedrooms') !== 'any') {
        params.bedrooms = searchParams.get('bedrooms');
      }
      if (searchParams.get('bathrooms') && searchParams.get('bathrooms') !== 'any') {
        params.bathrooms = searchParams.get('bathrooms');
      }
      if (searchParams.get('propertyType') && searchParams.get('propertyType') !== 'any') {
        params.propertyType = searchParams.get('propertyType');
      }
      if (searchParams.get('minPrice')) params.minPrice = parseInt(searchParams.get('minPrice'));
      if (searchParams.get('maxPrice')) params.maxPrice = parseInt(searchParams.get('maxPrice'));

      performSearch(params);
    }
    setInitialSearchDone(true);
  }, [searchParams, performSearch, initialSearchDone]);

  const handleSearch = async (e) => {
    e.preventDefault();

    // Don't proceed if auth is still loading
    if (authLoading) {
      return;
    }

    // Check guest search limit (same as home page - 1 free search)
    if (!user && guestSearchCount >= 1) {
      showNotification('You have used your free search. Please sign up or log in to continue searching properties.', 'info');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }

    // Validate inputs
    if (searchType === 'zipcode' && !zipCode.trim()) {
      showNotification('Please enter a ZIP code', 'error');
      return;
    }
    if (searchType === 'citystate' && (!city.trim() || !state)) {
      showNotification('Please enter both city and state', 'error');
      return;
    }

    // Build search params
    const params = {};
    if (searchType === 'zipcode') {
      params.zipCode = zipCode.trim();
    } else {
      params.city = city.trim();
      params.state = state;
    }
    if (bedrooms !== 'any') params.bedrooms = bedrooms;
    if (bathrooms !== 'any') params.bathrooms = bathrooms;
    if (propertyType !== 'any') params.propertyType = propertyType;
    if (minPrice) params.minPrice = parseInt(minPrice);
    if (maxPrice) params.maxPrice = parseInt(maxPrice);

    // Update URL
    updateUrlParams(params);

    // Perform search (increment guest count for new searches)
    await performSearch(params, true);
  };

  const handlePropertyClick = (property) => {
    // Check guest ROI limit (1 free ROI calculation from browse)
    if (!user && guestROICount >= 1) {
      showNotification('You have used your free ROI calculation. Please sign up or log in to analyze more properties.', 'info');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }

    // Track guest ROI calculations
    if (!user) {
      const newCount = guestROICount + 1;
      setGuestROICount(newCount);
      localStorage.setItem('guestBrowseROICount', newCount.toString());
    }

    // Navigate to form page with property address for ROI calculation
    const address = property.formattedAddress ||
      `${property.addressLine1}, ${property.city}, ${property.state} ${property.zipCode}`;

    navigate('/form', {
      state: {
        address: address,
        isGuest: !user,
        propertyPrice: property.price
      }
    });
  };

  const clearFilters = () => {
    setZipCode('');
    setCity('');
    setState('');
    setBedrooms('any');
    setBathrooms('any');
    setPropertyType('any');
    setMinPrice('');
    setMaxPrice('');
    setCitySuggestions([]);
    setShowCitySuggestions(false);
    setResults([]);
    setHasSearched(false);
  };

  return (
    <div className="browse-page">
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      <div className="browse-header">
        <h1>Browse Properties for Sale</h1>
        <p>Search for properties by location and estimate their ROI.</p>
      </div>

      <div className="browse-content">
        {/* Search Form */}
        <div className="search-panel">
          <form onSubmit={handleSearch}>
            {/* Location Type Toggle */}
            <div className="form-group">
              <label>Search By</label>
              <div className="toggle-buttons">
                <button
                  type="button"
                  className={`toggle-btn ${searchType === 'zipcode' ? 'active' : ''}`}
                  onClick={() => setSearchType('zipcode')}
                >
                  ZIP Code
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${searchType === 'citystate' ? 'active' : ''}`}
                  onClick={() => setSearchType('citystate')}
                >
                  City & State
                </button>
              </div>
            </div>

            {/* Location Inputs */}
            {searchType === 'zipcode' ? (
              <div className="form-group">
                <label htmlFor="zipCode">ZIP Code</label>
                <input
                  type="text"
                  id="zipCode"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="e.g., 90210"
                  maxLength={5}
                />
              </div>
            ) : (
              <>
                <div className="form-group city-autocomplete">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    value={city}
                    onChange={handleCityChange}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                    onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                    placeholder="e.g., Austin"
                    autoComplete="off"
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <ul className="city-suggestions">
                      {citySuggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          onClick={() => handleCitySuggestionClick(suggestion)}
                          className="city-suggestion-item"
                        >
                          {suggestion.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <select
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Filters */}
            <div className="form-group">
              <label htmlFor="bedrooms">Bedrooms</label>
              <select
                id="bedrooms"
                value={bedrooms}
                onChange={(e) => setBedrooms(e.target.value)}
              >
                <option value="any">Any</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5+</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="bathrooms">Bathrooms</label>
              <select
                id="bathrooms"
                value={bathrooms}
                onChange={(e) => setBathrooms(e.target.value)}
              >
                <option value="any">Any</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4+</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="propertyType">Property Type</label>
              <select
                id="propertyType"
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
              >
                <option value="any">Any</option>
                <option value="Single Family">Single Family</option>
                <option value="Condo">Condo</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Multi-Family">Multi-Family</option>
                <option value="Apartment">Apartment</option>
                <option value="Manufactured">Manufactured</option>
                <option value="Land">Land</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="minPrice">Min Price</label>
              <input
                type="number"
                id="minPrice"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="e.g., 200000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxPrice">Max Price</label>
              <input
                type="number"
                id="maxPrice"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="e.g., 500000"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="search-btn" disabled={isLoading}>
                {isLoading ? 'Searching...' : 'Search Properties'}
              </button>
              <button type="button" className="clear-btn" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Results */}
        <div className="results-panel">
          {isLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Searching for properties...</p>
            </div>
          )}

          {!isLoading && !hasSearched && (
            <div className="empty-state">
              <div className="empty-icon">🏠</div>
              <h3>Search for Properties</h3>
              <p>Enter a location to find properties.</p>
            </div>
          )}

          {!isLoading && hasSearched && results.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No Properties Found</h3>
              <p>Try adjusting your search criteria or searching a different location</p>
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <>
              <div className="results-header">
                <h3>{results.length} Properties Found</h3>
              </div>
              <div className="results-grid">
                {results.map((property) => (
                  <div
                    key={property.id}
                    className="property-card"
                    onClick={() => handlePropertyClick(property)}
                  >
                    {property.photos && property.photos.length > 0 ? (
                      <div className="property-image">
                        <img src={property.photos[0]} alt={property.formattedAddress} />
                      </div>
                    ) : (
                      <div className={`property-image placeholder ${(property.propertyType || '').toLowerCase().replace(/[^a-z]/g, '-')}`}>
                        <span>{getPropertyIcon(property.propertyType)}</span>
                      </div>
                    )}
                    <div className="property-info">
                      <div className="property-price">{formatPrice(property.price)}</div>
                      <div className="property-address">
                        {property.addressLine1}
                        <br />
                        {property.city}, {property.state} {property.zipCode}
                      </div>
                      <div className="property-details">
                        <span>{property.bedrooms || '—'} bed</span>
                        <span>{property.bathrooms || '—'} bath</span>
                        <span>{formatSquareFootage(property.squareFootage)}</span>
                      </div>
                      {property.propertyType && (
                        <div className="property-type">{property.propertyType}</div>
                      )}
                      {property.daysOnMarket && (
                        <div className="days-on-market">
                          {property.daysOnMarket} days on market
                        </div>
                      )}
                    </div>
                    <div className="property-cta">
                      <span className="cta-roi">Calculate ROI →</span>
                      <a
                        href={getZillowUrl(property)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cta-zillow"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on Zillow →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowsePropertiesPage;
